import { useEffect, useRef } from 'react'

// "Yes" saves (2026-10-06). A salon asked Bailey for Stories, said "yes", and
// was told to go to Social and save them there: the posts only saved with a
// tap on the card, and the model has no way to tap it. Now the things Bailey
// just put on screen (the latest reply's blocks) register how to save
// themselves, and a reply that says to (yes, save them, plan them, go for
// it...) saves them before it goes to Bailey, with a line saying exactly what
// was saved so Bailey confirms it truthfully.
//
// Saving here is what the card's own Save / Plan button does: drafts and
// planned posts in Social, drafts in Campaigns. Nothing is sent or spent.

const savers = new Map() // block id -> () => Promise<string|null> (what was saved, in words)

/** A widget in the latest reply says how to save what it shows. Returns the undo. */
export const registerSaver = (id, fn) => {
  if (!id || typeof fn !== 'function') return () => {}
  savers.set(id, fn)
  return () => { if (savers.get(id) === fn) savers.delete(id) }
}

export const hasSavers = () => savers.size > 0

/** Save everything the latest reply shows. Resolves to the lines saved. */
export const saveLatest = async () => {
  const fns = Array.from(savers.values())
  const out = await Promise.all(fns.map(fn => Promise.resolve().then(fn).catch(() => null)))
  return out.filter(Boolean)
}

// "yes", "yes please", "save them", "plan them all", "go for it", "perfect,
// schedule them"... but not "yes but make it pinker" or "no", and not a long
// message that only starts with yes (that's a new request).
const YES = /^(yes|yeah|yep|yup|ok|okay|sure|perfect|great|lovely|love (it|them)|looks good|sounds good|that'?s (great|perfect|good)|go (for it|ahead)|do it|please do|save( (it|them|all|those|these)( all)?)?|plan( (it|them|all|those|these)( all)?)?|schedule( (it|them|all|those|these)( all)?)?|book (it|them) in)\b/i
const BUT = /\b(but|change|instead|different|swap|less|more|not|no|don'?t|without|another|redo|again)\b/i

export const isSaveIntent = (text) => {
  const s = String(text || '').trim().toLowerCase().replace(/[!.,]+$/g, '')
  if (!s || s.length > 60) return false
  if (!YES.test(s)) return false
  // "yes" followed by a change is a change, not a save
  const rest = s.replace(YES, '')
  return !BUT.test(rest)
}

// A "yes" answers the last question Bailey asked. Ben's tidy turns closing
// questions into Yes please / No thanks buttons, so "Yes please" to "Want an
// email to match?" must not also save the Stories on screen. It saves when the
// last reply asked nothing, or asked about saving, planning or using them.
const SAVE_Q = /\b(save|saved|plan|planned|schedule|keep|use (them|it|these|those)|go with|happy with|look good|looks? right|all set|ready to go|lock (them|it) in|post (them|it)|share (them|it)|go ahead)\b/i
const lastQuestion = (reply) => {
  const words = String(reply || '').replace(/(```|~~~)[\s\S]*?\1/g, ' ')
  const qs = words.match(/[^.!?\n]*\?/g)
  return qs && qs.length ? qs[qs.length - 1].trim() : ''
}
export const yesSaves = (text, lastReply) => {
  if (!isSaveIntent(text)) return false
  // "save them", "plan them": they said what, whatever was asked
  if (/\b(save|plan|schedule|keep)\b/i.test(String(text))) return true
  const q = lastQuestion(lastReply)
  return !q || SAVE_Q.test(q)
}

/** The line the app adds to the turn's context after saving. */
export const savedLine = (lines) => (lines && lines.length
  ? `THE APP JUST SAVED what your last reply showed, because they said yes: ${lines.join('; ')}. Confirm it in one line, exactly as said here. Planned posts are not published automatically: Beu reminds them on the day and they share it from Social.`
  : '')

/**
 * In a widget: while this is the latest reply and there's something to save,
 * a "yes" saves it with `fn` (the card's own save). `fn` may change every
 * render; the latest one is used.
 */
export const useSaver = (id, active, fn) => {
  const ref = useRef(fn)
  ref.current = fn
  useEffect(() => {
    if (!active) return undefined
    return registerSaver(id, () => ref.current())
  }, [id, active])
}

// ─── Changes by asking (2026-10-06) ───
// "Less formal", "make them pink", "move it to Thursday": the cards Bailey
// just showed change in place (the same post, its photos and any studio
// edits kept) instead of Bailey making new ones. Post cards register an
// editor (the post studio's own Bailey edit, askAI); plan cards tell Bailey
// what's on screen (registerFacts) so it can answer with a plans block that
// edits that plan.
const editors = new Map() // block id -> (text) => Promise<string|null> (what changed, in words)
const facts = new Map() // block id -> () => string[] (what's on screen, for the turn's context)

export const registerEditor = (id, fn) => {
  if (!id || typeof fn !== 'function') return () => {}
  editors.set(id, fn)
  return () => { if (editors.get(id) === fn) editors.delete(id) }
}
export const hasEditors = () => editors.size > 0
export const editLatest = async (text) => {
  const fns = Array.from(editors.values())
  const out = await Promise.all(fns.map(fn => Promise.resolve().then(() => fn(text)).catch(() => null)))
  return out.filter(Boolean)
}
export const useEditor = (id, active, fn) => {
  const ref = useRef(fn)
  ref.current = fn
  useEffect(() => {
    if (!active) return undefined
    return registerEditor(id, (text) => ref.current(text))
  }, [id, active])
}

export const registerFacts = (id, fn) => {
  if (!id || typeof fn !== 'function') return () => {}
  facts.set(id, fn)
  return () => { if (facts.get(id) === fn) facts.delete(id) }
}
export const screenFacts = () => {
  const out = []
  facts.forEach((fn) => { try { out.push(...(fn() || [])) } catch (e) { /* skip */ } })
  return out
}
export const useFacts = (id, active, fn) => {
  const ref = useRef(fn)
  ref.current = fn
  useEffect(() => {
    if (!active) return undefined
    return registerFacts(id, () => ref.current())
  }, [id, active])
}

// A change to what's on screen, not a new thing. Short, says how it should
// look or when, and isn't asking for something else ("an email", "5 more").
const CHANGE = /\b(less|more|bolder|brighter|darker|lighter|calmer|softer|louder|warmer|cooler|formal|casual|chatty|fun|funnier|cheeky|playful|friendly|professional|serious|shorter|longer|simpler|punchier|colou?rs?|pink|blue|green|red|black|white|gold|purple|font|bigger|smaller|logo|photo|picture|emoji|emojis|headline|wording|words|tone|style|layout|feed post|stories|story|move|earlier|later|morning|evening|afternoon|monday|tuesday|wednesday|thursday|friday|saturday|sunday|tomorrow|today|change|swap|switch|instead|drop|remove|without|turn off|no text|no email)\b/i
const NEW_THING = /\b(new|another|\d+ more|more (posts|stories|ideas)|email|newsletter|campaign|journey|automation|week of|plan (a|my)|what should)\b/i
export const isChangeIntent = (text) => {
  const s = String(text || '').trim()
  if (!s || s.length > 140 || isSaveIntent(s)) return false
  if (/^(no|nope)\b[.!]*$/i.test(s)) return false
  return CHANGE.test(s) && !NEW_THING.test(s)
}

export const changedLine = (lines, asked) => (lines && lines.length
  ? `THE APP JUST CHANGED what your last reply showed, in place, as they asked ("${String(asked).slice(0, 120)}"): ${lines.join('; ')}. Confirm it in a line; don't make new ones unless they ask. Anything saved before stays saved, now changed.`
  : '')

// For tests
export const _reset = () => { savers.clear(); editors.clear(); facts.clear() }
